import { FC } from "react";

import { Stack } from "@mui/material";

import type { AssistantFeature } from "../../contexts/AssistantConfigContext";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ArrowForward, ChevronDown } from "@/icons/outlined";

interface PanelHeaderProps {
  feature: AssistantFeature;
  titleId: string;
  isStartNewDisabled: boolean;
  onStartNew?: () => void;
  onClose: () => void;
}

export const PanelHeader: FC<PanelHeaderProps> = ({ feature, titleId, isStartNewDisabled, onStartNew, onClose }) => {
  const { t } = useTranslate();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={(theme) => ({
        p: 2,
        bgcolor: theme.palette.surface.brand.intense,
        color: theme.palette.text.reverse.intense,
      })}
    >
      <DashboardTypography
        id={titleId}
        component="h2"
        variant="subtitle2"
        vibe="text-reverse-intense"
        sx={{ flex: 1, minWidth: 0 }}
      >
        {t("dashboard", "assistant.title")}
      </DashboardTypography>
      <DashboardTooltip
        title={t("dashboard", "assistant.widget.reset")}
        placement="top"
        enterDelay={0}
        describeChild
        slotProps={{ tooltip: { sx: (theme) => theme.typography.caption } }}
      >
        <Stack component="span">
          <DashboardIconButton
            size="small"
            variant="text"
            disabled={isStartNewDisabled || !onStartNew}
            aria-label={t("dashboard", "assistant.widget.reset")}
            data-test={`${feature}-new-conversation`}
            sx={(theme) => ({
              color: theme.palette.text.reverse.intense,
              "&:hover": { bgcolor: theme.palette.surface.brand.quiet, color: theme.palette.text.brand.intense },
            })}
            onClick={onStartNew}
          >
            <ArrowForward />
          </DashboardIconButton>
        </Stack>
      </DashboardTooltip>
      <DashboardIconButton
        size="small"
        variant="text"
        aria-label={t("dashboard", "assistant.widget.minimize")}
        data-test={`${feature}-close`}
        sx={(theme) => ({ color: theme.palette.text.reverse.intense })}
        onClick={onClose}
      >
        <ChevronDown />
      </DashboardIconButton>
    </Stack>
  );
};
